import { useCallback, useEffect } from 'react'
import { DateTime } from 'luxon'
import { Link } from 'react-router-dom'

import {
  getAdminCalendlyLinkByUserEventId,
  getAllUserCallEventById,
  getCurrentYearAllUserCallEventIds,
  getPastAllUserCallEventsIds,
  getCallEventName,
  getUserCallLabelColorClass,
} from '@/selectors/userCallEventSelectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  fetchAdminUserCallEventsIfNeeded,
  updateAdminUserCallEvent,
} from '@/actions/admin/userCallEventsActions'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { useReselector } from '@/utils/sharedHooks'

import { Card, Text, Button, Table, Label, CardColors, Separator } from '@/ui'
import { TypographyStyles } from '@/styles'

const UserCallRow = ({ id, userId }: { id: number; userId: number }) => {
  const dispatch = useAppDispatch()
  const userCall = useReselector(getAllUserCallEventById, userId, id)
  const calendlyLink = useReselector(
    getAdminCalendlyLinkByUserEventId,
    userId,
    id
  )

  const toggleMissed = useCallback(() => {
    if (!userCall?.id) return

    dispatch(
      updateAdminUserCallEvent(userCall.id, {
        missedOn: userCall.eventState === 'missed' ? null : Date.now(),
      })
    )
  }, [dispatch, userCall?.eventState, userCall?.id])

  if (!userCall) return null

  const { eventState, startTime, location } = userCall
  const startLuxon = startTime
    ? DateTime.fromISO(startTime, { zone: 'utc' }).toLocal()
    : null

  return (
    <Table.Row>
      <Table.Cell>{getCallEventName(userCall, calendlyLink)}</Table.Cell>
      <Table.Cell>
        <Label className={getUserCallLabelColorClass(eventState)}>
          {eventState}
        </Label>
      </Table.Cell>
      <Table.Cell>
        {startTime && (
          <>
            {startLuxon?.toFormat(DATE_FORMATS_LUXON.DISPLAY_LONG)}
            <br />
            {startLuxon?.toFormat(DATE_FORMATS_LUXON.TIME)}{' '}
            {startLuxon?.offsetNameShort}
          </>
        )}
      </Table.Cell>
      <Table.Cell>
        {location && (
          <a href={location} className="text-blue-600 hover:underline">
            {location.replace('https://', '')}
          </a>
        )}
      </Table.Cell>
      <Table.Cell>
        <Button variant="link" onClick={toggleMissed}>
          {eventState === 'missed' ? 'Unmark missed' : 'Mark missed'}
        </Button>
      </Table.Cell>
    </Table.Row>
  )
}

export const AdminUserCallsCard = ({
  userId,
  showPastEvents = false,
  showPastLink = true,
}: {
  userId: number
  showPastEvents?: boolean
  showPastLink?: boolean
}) => {
  const dispatch = useAppDispatch()

  const currentYearCallEventIds = useReselector(
    getCurrentYearAllUserCallEventIds,
    userId
  )
  const pastYearCallEventIds = useReselector(
    getPastAllUserCallEventsIds,
    userId
  )
  const ids = showPastEvents ? pastYearCallEventIds : currentYearCallEventIds

  useEffect(() => {
    dispatch(fetchAdminUserCallEventsIfNeeded(userId))
  }, [dispatch, userId])

  return (
    <Card color={CardColors.LEVEL_0} className="w-full">
      <div className="flex items-center justify-between">
        <Text variant={TypographyStyles.H2}>User Calls</Text>
        <br />
        {showPastLink && (
          <Button variant="link" asChild>
            <Link to={`/admin/finances/records/${userId}/calls`}>
              View Past
            </Link>
          </Button>
        )}
      </div>
      <Separator />
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Call name</Table.HeaderCell>
            <Table.HeaderCell>State</Table.HeaderCell>
            <Table.HeaderCell>Date/Time</Table.HeaderCell>
            <Table.HeaderCell>Link</Table.HeaderCell>
            <Table.HeaderCell>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {ids.map((id) => (
            <UserCallRow key={id} id={id} userId={userId} />
          ))}
        </Table.Body>
      </Table>
    </Card>
  )
}
